import {Location} from 'history'

import {isNil, isObject} from '@shared/helpers/lang'

import {defaultLocationState, sampleLocationState} from './constants'
import {CustomLocation, LocationState} from './interface'

const isCustomLocation = (location: Location): location is CustomLocation => {
    return !!location.state && typeof location.state === 'object'
}

const isPrimitive = (value: unknown): value is string | number | boolean => {
    return !(value === null || typeof value === 'object' || typeof value === 'function')
}

export const getLocationStateField = <K extends keyof CustomLocation['state']>(
    location: Location,
    key: K,
): LocationState[K] => {
    // 값이 null or undefined라면 undefined return
    if (!isCustomLocation(location) || isNil(location.state[key])) {
        return
    }

    // 값이 원시값이라면
    if (isPrimitive(location.state[key])) {
        // 타입이 일치하면 그대로 return
        if (typeof location.state[key] === typeof sampleLocationState[key]) {
            return location.state[key]
        }
        // 타입이 일치하지 않으면 기본값 return
        return defaultLocationState[key]
    }

    // 객체가 아니면 undefined return
    if (!isObject(location.state[key])) {
        return
    }

    const objectToReturn: {[props: string]: unknown} = {}
    // 객체를 순회하며, 타입을 검사한 후 통과하면 값 그대로 return
    // 타입 검사를 실패했다면
    // 1. optional type이라면 undefined return
    // 2. required type이라면 기본값 반환 (ex. 문자열이라면 '')
    for (const props in location.state[key]) {
        if (typeof location.state[key]![props] === typeof sampleLocationState[key][props]) {
            objectToReturn[props] = location.state[key]![props]
            continue
        }
        objectToReturn[props] = defaultLocationState[key]![props]
    }
    return objectToReturn as LocationState[K]
}

export const getLocationState = (location: Location) => {
    const state: LocationState = {}

    Object.keys(sampleLocationState).forEach((key) => {
        ;(state[key as keyof LocationState] as unknown) = getLocationStateField(location, key as keyof LocationState)
    })

    return state
}
